import { useQuery } from '@apollo/client'
import { Box, Skeleton, Stack, Typography, useTheme } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Architecture, Bucket, BucketDocument, BucketsByUserIdDocument } from '../../../../../graphql/generated'
import { modalVar } from '../../../../../providers/apollo/cache'
import Crown from '../../../../../svg/Crown'
import logger from '../../../../../utils/logger'
import useAlerts from '../../../hooks/useAlerts'
import useProfilePlan from '../../../hooks/useProfilePlan'
import BucketItem from './BucketItem'

interface Props {
  userId: string
  drawerSection?: 'main' | 'albums'
}

export default function BucketSwitcher({ userId }: Readonly<Props>) {
  const theme = useTheme()
  const { createAlert } = useAlerts()
  const { bucketId } = useParams<{ bucketId: string }>()
  const { isFreemiumUser, bucketLimit } = useProfilePlan()

  // Pagination state
  const [loadingMore, setLoadingMore] = useState(false)
  const [allBuckets, setAllBuckets] = useState<Bucket[]>([])
  const [currentNextToken, setCurrentNextToken] = useState<string | null | undefined>(undefined)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const results = useQuery(BucketsByUserIdDocument, {
    variables: { userId },
    onError: (e) => {
      logger.error(e)
      createAlert('We are having trouble retrieving your buckets. Please try again later.')
      setLoadingMore(false)
    },
    onCompleted: (data) => {
      if (data?.bucketsByUserId?.items) {
        setAllBuckets(data.bucketsByUserId.items)
        setCurrentNextToken(data.bucketsByUserId.nextToken)
      }
    }
  })

  // Update local state when query data changes (from cache updates or refetches)
  useEffect(() => {
    if (results.data?.bucketsByUserId?.items) {
      setAllBuckets(results.data.bucketsByUserId.items)
      setCurrentNextToken(results.data.bucketsByUserId.nextToken)
    }
  }, [results.data])

  // Check if active bucket is in the current list
  const activeBucketId = bucketId
  const isActiveBucketInResults = allBuckets.some((bucket) => bucket.id === activeBucketId)

  // Fetch active bucket separately if it's not in the first page
  const { data: activeBucketData } = useQuery(BucketDocument, {
    variables: { bucketId: activeBucketId || '' },
    skip: !activeBucketId || isActiveBucketInResults || !allBuckets.length,
    onError: (error) => {
      logger.error('Error fetching active bucket:', error)
    }
  })

  // Create display buckets with active bucket pinned to top
  const displayBuckets = useMemo(() => {
    let buckets = [...allBuckets]

    // Remove active bucket from list if it exists (prevent duplicates)
    if (activeBucketId) {
      buckets = buckets.filter((bucket) => bucket.id !== activeBucketId)
    }

    // Add active bucket to top if it exists
    const activeBucket = activeBucketData?.bucket || allBuckets.find((b) => b.id === activeBucketId)
    if (activeBucket) {
      buckets = [activeBucket, ...buckets]
    }

    return buckets
  }, [allBuckets, activeBucketId, activeBucketData?.bucket])

  const bucketsWithoutLegacy = displayBuckets.filter((bucket) => bucket.architecture !== Architecture.Kafka)

  const freemiumUserReachedMaxBuckets = isFreemiumUser && allBuckets.filter((b) => b.ownerId === userId).length >= bucketLimit
  const hasMore = !!currentNextToken

  const openModal = (modalName: 'createBucket' | 'bucketRename' | 'upsellModal') => {
    modalVar(modalName)
  }

  // Load more buckets when scrolling
  const loadMoreBuckets = useCallback(async () => {
    if (loadingMore || !currentNextToken) return

    setLoadingMore(true)
    try {
      const { data } = await results.fetchMore({
        variables: {
          userId,
          nextToken: currentNextToken
        }
      })

      if (data?.bucketsByUserId?.items) {
        // Append new buckets to local state
        setAllBuckets((prev) => [...prev, ...(data.bucketsByUserId?.items || [])])
        setCurrentNextToken(data.bucketsByUserId?.nextToken)
      }
    } catch (error) {
      logger.error('Error loading more buckets:', error)
      createAlert('Failed to load more buckets. Please try again.')
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, currentNextToken, results, userId, createAlert])

  // Set up IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!hasMore || loadingMore) {
      return undefined
    }

    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    }

    observerRef.current = new IntersectionObserver((entries) => {
      const [entry] = entries
      if (entry.isIntersecting && hasMore && !loadingMore) {
        loadMoreBuckets()
      }
    }, options)

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [hasMore, loadingMore, loadMoreBuckets])

  if (!results.called || results.loading) {
    return <Skeleton variant="rectangular" width="100%" height="50px" />
  }

  return (
    <Box height="100%" display="flex" flexDirection="column">
      {freemiumUserReachedMaxBuckets && (
        <Box
          sx={{
            position: 'relative',
            marginBottom: '12px'
          }}
        >
          {/* Dotted line above banner */}
          <Box
            sx={{
              position: 'absolute',
              top: '-8px',
              left: 0,
              right: 0,
              height: '2px',
              background: 'repeating-linear-gradient(to right, #DB54FF 0, #DB54FF 4px, transparent 4px, transparent 8px)',
              zIndex: 1
            }}
          />
          {/* Upgrade banner */}
          <Box
            sx={{
              background: theme.palette.groupsyPink[50],
              borderRadius: '8px',
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)'
              }
            }}
            onClick={() => openModal('upsellModal')}
          >
            <Crown sx={{ display: 'flex' }} size={20} color={theme.palette.groupsyPink[700]} />
            <Typography
              sx={{
                color: theme.palette.groupsyPink[700],
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: 'inherit',
                lineHeight: 1.2
              }}
            >
              Upgrade for unlimited Buckets.
            </Typography>
          </Box>
        </Box>
      )}
      <Box>
        <Stack
          direction="column"
          gap="6px"
          alignItems="flex-start"
          sx={{
            marginTop: '4px',
            width: '260px'
          }}
        >
          {bucketsWithoutLegacy.map((bucket) => (
            <BucketItem key={bucket.id} bucket={bucket} userId={userId} />
          ))}

          {/* Loading skeleton for pagination */}
          {loadingMore && (
            <>
              {[1, 2, 3].map((skeletonId) => (
                <Stack
                  key={`skeleton-bucket-loader-${skeletonId}`}
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    flexDirection: 'row',
                    width: '100%',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid transparent'
                  }}
                >
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1 }}>
                    <Stack alignItems="flex-start" sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="120px" height={28} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Skeleton variant="circular" width={20} height={20} />
                        <Skeleton variant="text" width="80px" height={16} />
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              ))}
            </>
          )}

          {/* Sentinel element for infinite scroll */}
          {hasMore && !loadingMore && (
            <div ref={sentinelRef} style={{ height: '1px', width: '100%' }}>
              &nbsp;
            </div>
          )}
        </Stack>
      </Box>
    </Box>
  )
}
